import {
  Badge,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  InlineStack,
  Link,
  List,
  ListItem,
  Text,
} from '@customerio/pluma-components/react';
import type { ColumnDef } from '@tanstack/react-table';
import type { DataTableFeatures } from '@customerio/pluma-components/react';
import plur from 'plur';
import { STATE_BADGE_COLORS } from '@/features/campaigns';

import { CampaignStateLabels } from '@/utils/campaign-labels';
import { capitalize } from '@/utils/string';
import { getDesignStudioTypeLabel } from '../utils/item';
import type { DataIndexUsageKey } from '../utils/types';
import { usageTarget, type UsageRecord } from './usage-links';

/**
 * The badge colours every campaign chip in the app reads, keyed by a bare
 * string: the state on this record is whatever the wire sent, not the union
 * the canonical map is written against.
 */
const STATE_COLORS: Record<string, 'grey' | 'yellow' | 'red' | 'green'> = STATE_BADGE_COLORS;

/**
 * What each usage surface lists.
 *
 * Every row is a name that links out to the thing itself, because the reason
 * to read this tab is to go and change what is in the way. A campaign and a
 * segment say a little more: a campaign in what capacity it uses the item,
 * and a segment whether it is still live.
 */

type UsageColumns = ColumnDef<DataTableFeatures, UsageRecord>[];

type UsageLinkProps = {
  href: string | null;
  name: string;
};

function UsageLink({ href, name }: UsageLinkProps) {
  if (href == null) {
    return <Text>{name}</Text>;
  }
  return (
    <Link href={href} variant="secondary" isUnderlined={false} icon="exit" iconPosition="trailing" target="_blank">
      {name}
    </Link>
  );
}

/** One name column, which is all most surfaces have. */
function nameColumn(cell: (row: UsageRecord) => React.ReactNode): UsageColumns[number] {
  return { id: 'name', header: 'Name', cell: ({ row }) => cell(row.original) };
}

/** How a campaign uses the item, counted: "Trigger", "2 filters". */
export function campaignUsageSummary(usage: readonly string[] | null | undefined): string[] {
  const counts = new Map<string, number>();
  for (const entry of usage ?? []) {
    counts.set(entry, (counts.get(entry) ?? 0) + 1);
  }
  return [...counts].map(([name, count]) => (count === 1 ? capitalize(name) : `${String(count)} ${plur(name, count)}`));
}

type CampaignUsageProps = {
  row: { original: UsageRecord };
};

/** The capacities one campaign uses the item in, under its row. */
export function CampaignUsage({ row }: CampaignUsageProps) {
  const campaign = row.original;
  if (!('campaign_id' in campaign)) {
    return null;
  }

  return (
    <List>
      {campaignUsageSummary(campaign.usage).map((usage) => (
        <ListItem key={usage} text="product-p-secondary">
          {usage}
        </ListItem>
      ))}
    </List>
  );
}

function campaignColumns(workspaceId: number): UsageColumns {
  return [
    nameColumn((row) => <UsageLink href={usageTarget(workspaceId, row).href} name={row.name} />),
    {
      id: 'state',
      header: 'State',
      cell: ({ row }) => {
        const campaign = row.original;
        if (!('campaign_id' in campaign) || campaign.state == null) {
          return null;
        }
        const state = campaign.state;
        // A transient backend state — `evaluating`, `paused`, `deleting` —
        // has no wording of its own, and reads better as itself than as
        // "Unknown". The badge on a campaign's own page does the same.
        // Dotted, as the page this replaces draws it: its
        // `campaign-state-label` passes `@showStatusIndicator={{true}}`.
        return (
          <Badge color={STATE_COLORS[state] ?? 'grey'} showStatusIndicator>
            {CampaignStateLabels[state] ?? capitalize(state)}
          </Badge>
        );
      },
    },
  ];
}

/**
 * A segment's own row. The archived ones still count as usage — they are
 * what stops an attribute being deleted — so they are listed and marked
 * rather than left out.
 */
function segmentColumns(workspaceId: number): UsageColumns {
  return [
    nameColumn((row) => (
      <InlineStack gap="100" alignBlock="center">
        <UsageLink href={usageTarget(workspaceId, row).href} name={row.name} />
        {'segment_id' in row && row.archived ? <Badge color="red">Archived</Badge> : null}
      </InlineStack>
    )),
  ];
}

/**
 * A surface whose rows are nothing but a name that links out: newsletters,
 * templates, snippets, layouts. Where the record opens is the one thing they
 * differ in, and `usageTarget` reads that off the record itself.
 */
function linkedNameColumns(workspaceId: number): UsageColumns {
  return [nameColumn((row) => <UsageLink href={usageTarget(workspaceId, row).href} name={row.name} />)];
}

function designStudioColumns(workspaceId: number): UsageColumns {
  return [
    nameColumn((row) => <UsageLink href={usageTarget(workspaceId, row).href} name={row.name} />),
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const asset = row.original;
        return 'short_id' in asset ? <Text>{getDesignStudioTypeLabel(asset.type)}</Text> : null;
      },
    },
  ];
}

/** Which surface shows which columns. `satisfies` is what keeps it exhaustive. */
const COLUMNS_BY_SURFACE = {
  campaigns: campaignColumns,
  segments: segmentColumns,
  newsletters: linkedNameColumns,
  templates: linkedNameColumns,
  snippets: linkedNameColumns,
  layouts: linkedNameColumns,
  design_studio: designStudioColumns,
} satisfies Record<DataIndexUsageKey, (workspaceId: number) => UsageColumns>;

/** The columns one usage surface shows. */
export function usageColumnsFor(view: DataIndexUsageKey, workspaceId: number): UsageColumns {
  return COLUMNS_BY_SURFACE[view](workspaceId);
}

type UsageEmptyStateProps = {
  surface: DataIndexUsageKey;
  /** What the surface is called, as the workspace calls it. */
  label: string;
  itemName: string;
  icon: 'campaigns' | 'segment' | 'newsletter' | 'email' | 'transactional-api';
};

/**
 * What a surface says when nothing uses the item there.
 *
 * Every surface but one says the same thing about itself, in lower case.
 * Design Studio is a product name and says something else entirely, so it
 * cannot be derived from the label the tab shows.
 */
function emptyCopy(surface: DataIndexUsageKey, label: string, itemName: string) {
  if (surface === 'design_studio') {
    return {
      title: 'No Design Studio activity',
      description: `${itemName} is not used in any Design Studio templates, messages, or components.`,
    };
  }
  const plural = label.toLowerCase();
  return { title: `No ${plural} available`, description: `${itemName} is not used in any ${plural}.` };
}

/** Nothing uses the item here, which is worth saying in the surface's own words. */
export function UsageEmptyState({ surface, label, itemName, icon }: UsageEmptyStateProps) {
  const copy = emptyCopy(surface, label, itemName);

  return (
    <EmptyState>
      <EmptyStateTitle icon={icon}>{copy.title}</EmptyStateTitle>
      <EmptyStateDescription>{copy.description}</EmptyStateDescription>
    </EmptyState>
  );
}

/**
 * The icon each surface's empty state is drawn with, as the page this
 * replaces draws them: the three content surfaces share one, and Design
 * Studio has its own (`data-index-item/{templates,snippets,layouts,design-studio}/index.gts`).
 */
export const USAGE_ICONS = {
  campaigns: 'campaigns',
  segments: 'segment',
  newsletters: 'newsletter',
  templates: 'transactional-api',
  snippets: 'transactional-api',
  layouts: 'transactional-api',
  design_studio: 'email',
} satisfies Record<DataIndexUsageKey, UsageEmptyStateProps['icon']>;
